import React, {ChangeEvent, useCallback, useMemo, useRef, useState, Fragment} from 'react';
import cx from 'classnames';
import {RuleFilters} from '../../../../../enums';
import {overviewDialogID} from '../../../../../constants';
import {
    defaultDropDownV2Context,
    defaultSearchFilter,
    DropdownButtonTrigger,
    DropdownItem,
    DropdownV2,
    DropDownV2Context,
    Icon,
    Input,
    isSelected,
    PriorityIcon,
    useDropdown,
    Text,
    useKeyboardNavigation,
    CheckBox
} from '@priority-software/priority-style-react';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {AutomationChoose} from '../../../../../reducer/model';
import {ExpandOptions} from './ExpandOptions';
import {findFocusedAction, isExpandableItemChecked, isIndeterminated} from './utils';
import {ExpandableOptionType, OptionsSchemeItem} from './types';
import {texts} from '../../../../../texts';
import styles from './styles.module.scss';

type Props = {
    onFilterSelect: (data: AutomationChoose, isSelected: boolean, name: string) => void;
    values: Array<string | number>;
    options: {users: Array<AutomationChoose>; groups: Array<AutomationChoose>};
    contentComponentClasses?: string;
    onOpen: () => void;
    onClose: () => void;
    searchPlaceholder?: string;
};

const name = RuleFilters.validFor;
const dropdownMenuStyle = {width: 275};

const ValidForMainValue = {
    ValidAllUsers: texts.all_users_label,
    ValidMySelf: texts.myself_label,
    ValidUser: texts.specific_user_label,
    ValidGroup: texts.spec_privilege_group_label
};

const mainOptions = [
    {label: texts.myself_label, value: ValidForMainValue.ValidMySelf},
    {label: texts.all_users_label, value: ValidForMainValue.ValidAllUsers},
    {label: texts.valid_for_filter_user, value: ValidForMainValue.ValidUser},
    {label: texts.valid_for_filter_privilege_group, value: ValidForMainValue.ValidGroup}
];

const eventTypes = ['touchstart', 'mousedown'];

export const ValidForFilterDropdown = (props: Props) => {
    const {isOpen, toggleOpen, closeDropdown, openDropdown} = useDropdown({
        onClose: props.onClose,
        onOpen: props.onOpen
    });

    const {values, options, onFilterSelect, contentComponentClasses, searchPlaceholder} = props;
    const [searchValue, setSearchValue] = useState('');
    const [showUserOption, toggleShowUserOption] = useState(false);
    const [showGroupOption, toggleShowGroupOption] = useState(false);
    const inputRef = useRef<HTMLInputElement>();

    const searchString = searchValue.toLocaleLowerCase();

    const usersFilteredOptions = searchString
        ? options.users.filter(item => defaultSearchFilter(item, searchString))
        : options.users;

    const groupsFilteredOptions = searchString
        ? options.groups.filter(item => defaultSearchFilter(item, searchString))
        : options.groups;

    const getOptionsScheme = useCallback((): OptionsSchemeItem[] => {
        let i = -1;
        const optionsScheme = mainOptions.slice(0, 2);

        const userMainOption = mainOptions[2];
        (userMainOption as OptionsSchemeItem).data = {
            index: 2,
            additionalOptions: usersFilteredOptions as Array<ExpandableOptionType>,
            showOptions: showUserOption
        };

        const groupMainOption = mainOptions[3];
        (groupMainOption as OptionsSchemeItem).data = {
            index: 3,
            additionalOptions: groupsFilteredOptions as Array<ExpandableOptionType>,
            showOptions: showGroupOption
        };
        optionsScheme.push(userMainOption, groupMainOption);
        const mapperFunction = (item: OptionsSchemeItem, calcIndex: boolean) => {
            const index = calcIndex ? ++i : undefined;
            const showOptions = item.data?.showOptions;

            if (!item.data) {
                item.data = {};
            }

            return {
                ...item,
                data: {
                    ...item.data,
                    index,
                    additionalOptions: item.data?.additionalOptions?.map(additionalItem =>
                        mapperFunction(additionalItem, showOptions)
                    )
                }
            };
        };

        return optionsScheme.map(item => mapperFunction(item, true));
    }, [usersFilteredOptions, groupsFilteredOptions, showGroupOption, showUserOption]);

    const optionScheme = useMemo(getOptionsScheme, [getOptionsScheme]);

    const onSelect = useCallback(
        (option: AutomationChoose, selected: boolean) => {
            if (option.value === ValidForMainValue.ValidUser) {
                // Toggle user group options
                toggleShowUserOption(state => !state);
            } else if (option.value === ValidForMainValue.ValidGroup) {
                // Toggle user group options
                toggleShowGroupOption(state => !state);
            } else {
                // select ordinary value
                onFilterSelect(option, selected, name);
            }
            inputRef.current.focus();
            setFocusedIndex((option.data as {index?: number})?.index || 0);
        },
        [onFilterSelect]
    );

    const handleEnter = useCallback(
        (e: KeyboardEvent, focusedIndex: number) => {
            const focusedOption = findFocusedAction(getOptionsScheme(), focusedIndex);

            if (focusedOption) {
                onSelect(focusedOption, !isSelected(focusedOption, values, true));
            }
        },
        [onSelect, values, getOptionsScheme]
    );

    const getOptionsCount = () => {
        const lastItem = optionScheme[optionScheme.length - 1];
        const additionalOptions = lastItem.data?.additionalOptions || [];
        const lastIndex =
            additionalOptions[additionalOptions.length - 1]?.data?.index || lastItem?.data?.index;

        return lastIndex ? lastIndex + 1 : 0;
    };

    const {focusedIndex, handleNavigationKeys, setFocusedIndex} = useKeyboardNavigation({
        optionsCount: getOptionsCount(),
        defaultFocusedIndex: 0,
        isMulti: true,
        isOpen,
        handleEnter
    });

    const onSearchChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const {value} = e.target;
            setSearchValue(value);

            if (value) {
                toggleShowUserOption(!!value);
                toggleShowGroupOption(!!value);
            }
            setFocusedIndex(0);
        },
        [setFocusedIndex]
    );

    const handleClose = useCallback(() => {
        closeDropdown();
        setSearchValue('');
        setFocusedIndex(0);
        toggleShowUserOption(false);
        toggleShowGroupOption(false);
    }, [closeDropdown, setFocusedIndex]);

    const onGroupCheckboxChange = useCallback(e => {
        e.stopPropagation();
        e.preventDefault();
    }, []);

    const expandableItemRenderer = useCallback(
        (data: DropdownItemData) => {
            const hasAdditionalOptions = !!(data.data as any)?.additionalOptions;
            const additionalFilteredOption = hasAdditionalOptions
                ? (data.data as any)?.additionalOptions
                : [];

            const isChecked = isExpandableItemChecked(additionalFilteredOption, values) || false;

            let additionalOptions = [];

            if (data.value === ValidForMainValue.ValidUser) {
                additionalOptions = options.users;
            } else if (data.value === ValidForMainValue.ValidGroup) {
                additionalOptions = options.groups;
            }

            return (
                <>
                    <div onClick={onGroupCheckboxChange}>
                        <CheckBox
                            checked={isChecked}
                            indeterminate={
                                !isChecked &&
                                hasAdditionalOptions &&
                                isIndeterminated(additionalOptions, values)
                            }
                            disabled
                        />
                    </div>

                    <div className={styles.expandableItem}>
                        <Icon icon={PriorityIcon.arrowDown} iconColor="secondary-500" />
                        <Text tag="span" componentClasses={styles.label} skin="paragraph-2">
                            {data.label}
                        </Text>
                    </div>
                </>
            );
        },
        [onGroupCheckboxChange, values, options.users, options.groups]
    );

    return (
        <DropDownV2Context.Provider
            value={{
                ...defaultDropDownV2Context,
                isOpen,
                toggleOpen,
                closeDropdown: handleClose,
                openDropdown
            }}
        >
            <DropdownV2
                onClose={handleClose}
                isOpen={isOpen}
                onKeyDown={handleNavigationKeys}
                contentComponentClasses={cx(
                    contentComponentClasses,
                    styles.hasSearch,
                    styles.hasExtraPadding
                )}
                usePortal
                portalSelector={overviewDialogID}
                trigger={<DropdownButtonTrigger content={texts.valid_for_label} />}
                contentStyle={dropdownMenuStyle}
                eventTypes={eventTypes}
            >
                {isOpen && (
                    <div className={styles.contentBody}>
                        <div className={styles.search}>
                            <Input
                                inputRef={inputRef}
                                autoFocus
                                roundedCorners
                                value={searchValue}
                                onChange={onSearchChange}
                                placeholder={searchPlaceholder || texts.search_placeholder}
                            >
                                <Icon icon={PriorityIcon.searchThin} iconColor="secondary-400" />
                            </Input>
                        </div>

                        {optionScheme.map(item => {
                            const isExpandable = item.data?.showOptions;
                            const hasAdditionalOptions = !!item.data?.additionalOptions;

                            return (
                                <Fragment key={`${item.value}-${item.label}`}>
                                    <DropdownItem
                                        isMulti={!hasAdditionalOptions}
                                        data={item}
                                        isFocused={focusedIndex === item.data?.index}
                                        isSelected={isSelected(item, values, true)}
                                        onSelect={onSelect}
                                        itemBorder={false}
                                        contentRenderer={
                                            hasAdditionalOptions
                                                ? expandableItemRenderer
                                                : undefined
                                        }
                                    />

                                    {isExpandable && (
                                        <ExpandOptions
                                            key={`${item.value}-${item.label}-1`}
                                            values={values}
                                            data={item.data?.additionalOptions || []}
                                            onSelect={onSelect}
                                            searchString={searchString}
                                            focusedIndex={focusedIndex}
                                        />
                                    )}
                                </Fragment>
                            );
                        })}
                    </div>
                )}
            </DropdownV2>
        </DropDownV2Context.Provider>
    );
};
